import type { SelectableInvestmentListEntry } from "@eu.mdotm.api/sphere";
import {
	type TableColumn,
	AutoSortTable,
	AutoTooltip,
	DebouncedSearchInput,
	Dialog,
	DialogFooter,
	TableDataCell,
	TooltipContent,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { useSearchable } from "@mdotm/shadcn/legacy/headless";
import {
	type ClassList,
	type SpawnResult,
	adaptAnimatedNodeProvider,
	spawn,
	toClassListRecord,
} from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import type { MaybePromise } from "@mdotm/utils/promise";
import { Map, Set } from "immutable";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconWalls } from "$root/components/IconWall";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { usePortfolioColumns } from "$root/pages/PortfoliosStudio/PortfolioList/columns";
import type { RequireProperty } from "$root/utils";
import type { Instrument, InstrumentEntities } from "../const";
import { Button, SubmitButton } from "@mdotm/shadcn/mdotm/components";

type HealthySelectableInvestmentListEntry = RequireProperty<SelectableInvestmentListEntry, "uuid">;
/** @internal — exported for tests */
export type PortfoliosAdderDialogProps = {
	uuid?: string;
	onSubmitAsync(investments: Array<HealthySelectableInvestmentListEntry>): MaybePromise<void>;
	instrumentsInComposition: Instrument[];
	entity: InstrumentEntities;
} & MinimumDialogProps;

/** @internal — exported for tests */
export function lowerRowOpacity(
	fn: (row: HealthySelectableInvestmentListEntry, index: number) => ClassList | undefined,
) {
	return function inner(row: HealthySelectableInvestmentListEntry, index: number): ClassList | undefined {
		return {
			...toClassListRecord(fn(row, index)),
			"opacity-60": !row.canUseAsMixedPortfolio,
		};
	};
}

/** @internal — exported for tests */
export function PortfoliosAdderDialog({
	show,
	onClose,
	onSubmitAsync,
	uuid,
	entity,
	instrumentsInComposition,
	onAnimationStateChange,
}: PortfoliosAdderDialogProps) {
	const [query, setQuery] = useState("");

	const { t } = useTranslation();

	const preSelectedInstruments = useMemo(
		() =>
			Set(
				instrumentsInComposition.flatMap((x) =>
					x.tickerName && x.proxyOverwriteType === "PORTFOLIO_MIXED" ? [x.tickerName] : [],
				),
			),
		[instrumentsInComposition],
	);

	const querySelectablePortfolios = portfolioRepository.editorAvailablePortfolios.useQuery(uuid ?? null, entity);

	const { healthyRows, rows } = useMemo<{
		rows: Array<HealthySelectableInvestmentListEntry>;
		healthyRows: Array<HealthySelectableInvestmentListEntry>;
	}>(() => {
		const selectablePortfolios = querySelectablePortfolios.data?.flatMap(
			(x): HealthySelectableInvestmentListEntry[] => {
				const portfolioUuid = x.uuid;
				return portfolioUuid ? [{ ...x, uuid: portfolioUuid }] : [];
			},
		);

		if (!selectablePortfolios) {
			return { rows: [], healthyRows: [] };
		}

		const healthySelectable = selectablePortfolios?.filter((x) => x.canUseAsMixedPortfolio && x.uuid);
		return { rows: selectablePortfolios, healthyRows: healthySelectable };
	}, [querySelectablePortfolios]);

	const { filtered } = useSearchable({
		query,
		collection: healthyRows,
		extractFn: (item) => Object.values(item).join(" "),
	});

	const selectableRowIds = useMemo(
		() => healthyRows.flatMap((row) => (row.uuid && !preSelectedInstruments.has(row.uuid) ? [row.uuid] : [])),
		[healthyRows, preSelectedInstruments],
	);

	const selectableTableColumn = useSelectableTableColumn({
		filteredRows: filtered,
		rows: healthyRows,
		selectBy: (row) => row.uuid ?? "",
		selectableRowIds,
		// alwaysSelected: preSelectedInstruments,
	});

	const portfolioColumn = usePortfolioColumns();

	const column = useMemo<TableColumn<HealthySelectableInvestmentListEntry, string>[]>(
		() => [
			selectableTableColumn.column,
			{
				...portfolioColumn.NAME(),
				content: (row, cellProps) => {
					if (!row.canUseAsMixedPortfolio && row.currentlyContainsNestedPortfolios) {
						return (
							<AutoTooltip
								mode="hover"
								align="startToStart"
								position="top"
								overrideColor={themeCSSVars.palette_N200}
								trigger={({ innerRef }) => (
									<TableDataCell {...cellProps}>
										<span ref={innerRef}>{row.name}</span>
									</TableDataCell>
								)}
							>
								<TooltipContent>
									<p className="text-center">
										You cannot add portfolios that already include other portfolios in their metrics and performance
										calculation
									</p>
								</TooltipContent>
							</AutoTooltip>
						);
					}

					return row.name;
				},
			},
			portfolioColumn.STATUS(),
			portfolioColumn.LAST_STATUS_UPDATE(),
		],
		[portfolioColumn, selectableTableColumn.column],
	);

	const onSubmitSelectedPortfolios = useCallback(
		async (list: RequireProperty<SelectableInvestmentListEntry, "uuid">[], ctx: Immutable.Set<string>) => {
			const selectedPortfolios = ctx.toArray();
			const selectableInstrumentMap = Map(
				list?.flatMap((portfolio) => (portfolio.uuid ? [[portfolio.uuid, portfolio]] : [])),
			);
			const portfolios = selectedPortfolios.flatMap((portfolioUuid) => {
				const selectedInstrument = selectableInstrumentMap.get(portfolioUuid);
				return selectedInstrument ? [selectedInstrument] : [];
			});
			await onSubmitAsync?.(portfolios);
		},
		[onSubmitAsync],
	);

	return (
		<Dialog
			size="xxlarge"
			noValidate
			show={show}
			onClose={onClose}
			header="Select"
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
					primaryAction={
						<SubmitButton disabled={querySelectablePortfolios.isFetching}>{t("BUTTON.CONFIRM")}</SubmitButton>
					}
				/>
			}
			onAnimationStateChange={onAnimationStateChange}
			onSubmitAsync={() =>
				onSubmitSelectedPortfolios(
					rows.filter((x) => selectableRowIds.includes(x.uuid)),
					selectableTableColumn.multiSelectCtx.selection,
				)
			}
		>
			<div className="h-[calc(100dvh_-_214px)] flex-1 flex flex-col">
				<ReactQueryWrapperBase query={querySelectablePortfolios}>
					{() => (
						<>
							<div className="mb-4">
								<DebouncedSearchInput
									externalQuery={query}
									onChange={setQuery}
									placeholder={placholderSearch}
									className="max-w-[280px]"
									data-qualifier="PortfolioModal/Input/Search"
								/>
							</div>
							<AutoSortTable
								rows={filtered}
								rowClassName={lowerRowOpacity(selectableTableColumn.rowClassName)}
								columns={column}
								onRowClick={(row) => selectableTableColumn.toggle(row.uuid ?? "")}
								noDataText={<IconWalls.PortfolioListEmptyData />}
							/>
						</>
					)}
				</ReactQueryWrapperBase>
			</div>
		</Dialog>
	);
}

const placholderSearch = "Search a portfolio";

export type SpawnPortfoliosAdderDialogProps = Omit<PortfoliosAdderDialogProps, "show" | "onClose">;

export function spawnPortfoliosAdderDialog(props: SpawnPortfoliosAdderDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<PortfoliosAdderDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (portfolio) => {
					await props.onSubmitAsync(portfolio);
					resolve();
				}}
			/>
		)),
	);
}
